import React, { useEffect, useRef, useState } from "react";
import {
  AcquisitionCost,
  AcquisitionCostCategory,
  AcquisitionCostChannel,
  AcquisitionCostInput,
  CATEGORY_OPTIONS,
  SEGMENT_LABELS,
} from "../../../types/unit-economics.types";
import {
  extractErrorMessage,
  useCreateAcquisitionCostMutation,
  useUpdateAcquisitionCostMutation,
} from "../../../hooks/useUnitEconomics";

const todayDateInput = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toDateInput = (iso: string): string => {
  const date = new Date(iso);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

interface AcquisitionCostModalProps {
  cost: AcquisitionCost | null; // null → create mode
  onClose: () => void;
}

const AcquisitionCostModal: React.FC<AcquisitionCostModalProps> = ({
  cost,
  onClose,
}) => {
  const isEdit = !!cost;
  const createMutation = useCreateAcquisitionCostMutation();
  const updateMutation = useUpdateAcquisitionCostMutation();

  const [amountPln, setAmountPln] = useState<string>(
    cost ? (cost.amountCents / 100).toFixed(2) : "0.00"
  );
  const [incurredAt, setIncurredAt] = useState<string>(
    cost ? toDateInput(cost.incurredAt) : todayDateInput()
  );
  const [category, setCategory] = useState<AcquisitionCostCategory>(
    cost?.category ?? "pozyskanie_klienta"
  );
  const [description, setDescription] = useState<string>(
    cost?.description ?? ""
  );
  const [channel, setChannel] = useState<AcquisitionCostChannel | "">(
    cost?.channel ?? ""
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [amountTouched, setAmountTouched] = useState<boolean>(false);
  const [channelTouched, setChannelTouched] = useState<boolean>(false);

  const dialogRef = useRef<HTMLDivElement>(null);

  const parsedAmount = parseFloat(amountPln);
  const amountCents = Number.isNaN(parsedAmount)
    ? 0
    : Math.round(parsedAmount * 100);
  // Inline validation: empty or non-positive amount blocks submit. Only
  // surfaced after the user has touched the field, to avoid nagging on mount.
  const amountError =
    amountTouched && amountCents <= 0
      ? "Podaj kwotę większą od zera."
      : null;
  // Channel is required on every save (new and legacy edits). Error shown only
  // after blur to avoid nagging on mount.
  const channelError =
    channelTouched && !channel ? "Wybierz kanał." : null;
  const isPending = createMutation.isPending || updateMutation.isPending;
  const canSubmit = amountCents > 0 && !!incurredAt && !!channel && !isPending;

  // Close on Escape key.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Focus first field on mount.
  useEffect(() => {
    const firstInput = dialogRef.current?.querySelector<HTMLElement>(
      "input, select, textarea, button"
    );
    firstInput?.focus();
  }, []);

  // Keep Tab focus trapped within the dialog.
  const handleTrapKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'input, select, textarea, button, [href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute("disabled"));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitError(null);

    const input: AcquisitionCostInput = {
      amountCents,
      incurredAt,
      category,
      // canSubmit guards !!channel, so the cast is safe here
      channel: channel as AcquisitionCostChannel,
      description: description.trim() || undefined,
    };

    if (isEdit && cost) {
      updateMutation.mutate(
        { id: cost.id, input },
        {
          onSuccess: () => onClose(),
          onError: (error) =>
            setSubmitError(
              extractErrorMessage(error, "Błąd aktualizacji kosztu")
            ),
        }
      );
    } else {
      createMutation.mutate(input, {
        onSuccess: () => onClose(),
        onError: (error) =>
          setSubmitError(extractErrorMessage(error, "Błąd dodawania kosztu")),
      });
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ac-modal-title"
      onKeyDown={handleTrapKeyDown}
    >
      <div ref={dialogRef} className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3
            id="ac-modal-title"
            className="text-lg font-semibold text-neutral-900"
          >
            {isEdit ? "Edytuj koszt pozyskania" : "Dodaj koszt pozyskania"}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label
              htmlFor="ac-amount"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Kwota (PLN)
            </label>
            <input
              id="ac-amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amountPln}
              onChange={(e) => setAmountPln(e.target.value)}
              onBlur={() => setAmountTouched(true)}
              aria-invalid={amountError ? true : undefined}
              aria-describedby={amountError ? "ac-amount-error" : undefined}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
            {amountError && (
              <p id="ac-amount-error" className="mt-1 text-xs text-red-600">
                {amountError}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="ac-date"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Data
            </label>
            <input
              id="ac-date"
              type="date"
              value={incurredAt}
              onChange={(e) => setIncurredAt(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          <div>
            <label
              htmlFor="ac-category"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Kategoria
            </label>
            <select
              id="ac-category"
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as AcquisitionCostCategory)
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="ac-channel"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Kanał
            </label>
            <select
              id="ac-channel"
              value={channel}
              onChange={(e) =>
                setChannel(e.target.value as AcquisitionCostChannel | "")
              }
              onBlur={() => setChannelTouched(true)}
              aria-invalid={channelError ? true : undefined}
              aria-describedby={channelError ? "ac-channel-error" : undefined}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            >
              <option value="">— Wybierz kanał —</option>
              <option value="inbound">{SEGMENT_LABELS.inbound}</option>
              <option value="outbound">{SEGMENT_LABELS.outbound}</option>
            </select>
            {channelError && (
              <p id="ac-channel-error" className="mt-1 text-xs text-red-600">
                {channelError}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="ac-description"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Opis (opcjonalnie)
            </label>
            <textarea
              id="ac-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="np. kampania Google Ads, prowizja partnera"
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          {submitError && (
            <p
              role="alert"
              className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700"
            >
              {submitError}
            </p>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white px-4 py-2 rounded-md"
            >
              {isPending ? "Zapisywanie..." : isEdit ? "Zapisz" : "Dodaj"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AcquisitionCostModal;
